import styled from 'styled-components';
import { MarkdownBody } from './components/AnnouncementView.styled';

/**
 * Cards side by side on a wide screen, one column on a phone - the merchant
 * app's What's New list, given the room a desktop has. Aligned to the top:
 * bodies run to different lengths, and stretched cards would pad the short
 * ones with empty white.
 */
export const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  align-items: start;
  gap: 24px;
  margin: 32px 0;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    gap: 16px;
    margin: 20px 0;
  }
`;

export const Card = styled.article`
  background: ${({ theme }) => theme.white};
  border-radius: 20px;
  box-shadow: ${({ theme }) => theme.shadows.shadow1};
  overflow: hidden;
`;

/** Keeps the picture inside the card's padding, like the modal's. */
export const CardMediaFrame = styled.div`
  padding: 24px 24px 0;
`;

/**
 * The picture the way the announcement modal draws it (`HeroImage`): full
 * width, filling its box, softly rounded - so a card and the modal Read more
 * opens show the same thing. One fixed height instead of the modal's
 * max-height, so the cards of a row line up whatever the picture's shape.
 */
export const CardMedia = styled.img`
  display: block;
  width: 100%;
  height: 240px;
  object-fit: cover;
  border-radius: 16px;

  @media (max-width: 800px) {
    height: 200px;
  }
`;

export const CardBody = styled.div`
  padding: 20px 24px 24px;
  text-align: left;
`;

export const CardDate = styled.time`
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  color: ${({ theme }) => theme.lightGray};
`;

export const CardTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
  color: ${({ theme }) => theme.darkGray};
`;

export const LoadError = styled.p`
  margin: 32px 0;
  text-align: center;
  font-size: 14px;
  color: ${({ theme }) => theme.lightGray};
`;

export const RetryButton = styled.button`
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  color: ${({ theme }) => theme.primary};
  text-decoration: underline;
  cursor: pointer;
`;

/** Five lines of the body's 14px / 1.5 text. */
const CLAMPED_HEIGHT = 'calc(14px * 1.5 * 5)';

/**
 * The shared markdown styles, cut to five lines; Read more opens the whole
 * announcement in a modal.
 *
 * A max-height rather than `line-clamp`: the body is markdown, so paragraphs
 * and lists are separate blocks, and line-clamp only counts the lines of one.
 * The cut fades out instead of slicing a line in half.
 */
export const CardMarkdown = styled(MarkdownBody)<{ $faded: boolean }>`
  margin-bottom: 0;
  max-height: ${CLAMPED_HEIGHT};
  overflow: hidden;

  ${({ $faded }) =>
    $faded &&
    `
    -webkit-mask-image: linear-gradient(to bottom, #000 60%, transparent);
    mask-image: linear-gradient(to bottom, #000 60%, transparent);
  `}
`;

/** Read more, centred under the cut-off text. */
export const ReadMoreRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 12px;

  button {
    width: auto;
    font-weight: 600;
  }
`;
